import clsx from 'clsx';
import { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { UseFieldArrayRemove, UseFormRegister } from 'react-hook-form';
import { TextField } from '@/components/TextField';
import { BlockButton } from '@/components/BlockButton';
import { Textarea } from '@/components/Textarea';
import type { ProvidedJobPostsFormSchema } from '../schemas/providedJobPostsFormSchema';

type Props = {
  id: string;
  field: {
    key: string;
    content?: string;
  };
  index: number;
  register: UseFormRegister<ProvidedJobPostsFormSchema>;
  remove: UseFieldArrayRemove;
};

export default function JobContentItem({ id, field, index, register, remove }: Props) {
  const [isEdit, setIsEdit] = useState(false);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    background: isDragging ? '#F6F6F6' : '#FFFFFF',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className={clsx(
        'flex items-center rounded bg-white px-4 shadow-[0px_0px_6px_0px_#00000033] hover:bg-gray!',
        isEdit ? 'h-[315px]' : 'h-[58px]'
      )}
    >
      <div className="mr-2 py-4" {...listeners}>
        <img
          src="/agent/provided_job_posts/icon-drop.svg"
          width={24}
          height={24}
          alt=""
          className="cursor-grab active:cursor-grabbing"
        />
      </div>
      <div className="flex-1 py-4" onClick={() => setIsEdit((prev) => !prev)}>
        {isEdit ? (
          <div className="relative flex w-full items-center gap-3">
            <div className="flex flex-1 flex-col gap-3">
              <div className="flex flex-col gap-2">
                <p className="text-sm font-bold">タイトル</p>
                <div className="flex w-[496px]">
                  <TextField
                    {...register(`jobContentJson.${index}.key`)}
                    placeholder="営業"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.stopPropagation();
                      }
                    }}
                  />
                </div>
              </div>
              <div className="flex w-full grow flex-col gap-2">
                <p className="text-sm font-bold">詳細</p>
                <div className="flex h-[171px] w-full">
                  <Textarea
                    className="size-full resize-none p-3"
                    {...register(`jobContentJson.${index}.content`)}
                    rows={10}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.stopPropagation();
                      }
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="absolute top-0 right-0 w-[76px]">
              <BlockButton
                type="button"
                icon="/agent/provided_job_posts/icon-delete.svg"
                iconSize={20}
                height={42}
                text="削除"
                colorType="outlineRed"
                onClick={(e) => {
                  e.stopPropagation();
                  remove(index);
                }}
              />
            </div>
          </div>
        ) : (
          <div className="flex w-full items-center">
            <p className="ml-3 text-sm font-bold text-nowrap">{field.key}</p>
            <div className="ml-4 min-w-0 shrink overflow-hidden">
              <p className="line-clamp-1 w-full text-sm text-sub">
                {field.content === '' ? '詳細テキスト未入力' : field.content}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
